import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Sidebar from '../components/Sidebar';
import TopButtons from '../components/common/TopButtons';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ethers } from 'ethers';
import { usePublicClient, useChainId } from 'wagmi';
import { PublicClient } from 'viem';
import YOLOGame from '../config/YOLOGame.json';
import YOLOToken from '../config/YOLOToken.json';
import YOLOFactory from '../config/YOLOPadFactory.json';
import { getNativeToken,getFactoryAddress,getTradeUrl} from '../config/contracts';
import ProgressBar from '../components/common/ProgressBar';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { config } from 'process';
import Loading from '../components/common/Loading'; 
import { useAccount } from 'wagmi';
import { type Config, useConnectorClient } from 'wagmi'
import { clientToSigner } from '../utils/provider'; // 
import {
  useConnectModal,
  useAccountModal,
  useChainModal,
} from '@rainbow-me/rainbowkit';
import { useToast } from '../contexts/ToastContext';

/** Hook to convert a viem Wallet Client to an ethers.js Signer. */
export function useEthersSigner({ chainId }: { chainId?: number } = {}) {
  const { data: client } = useConnectorClient<Config>({ chainId })
  return useMemo(() => (client ? clientToSigner(client) : undefined), [client])
}

ChartJS.register(ArcElement, Tooltip, Legend);

const HomeTopButtons = styled(TopButtons)``;

const GameContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #0a0a1a;
  color: white;
  text-align: center;
  padding: 10px 10px;
  margin-left: 240px;

  @media (max-width: 768px) {
    margin-left: 0;
    padding-bottom: 80px;
  }
`;

const GameContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

// Token Info 相关样式
const TokenInfoSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  margin-bottom: 24px;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    padding: 4px;
  }
`;

const FavoriteIcon = styled.button<{ isFavorite: boolean }>`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.isFavorite ? '#FFD700' : 'rgba(255, 255, 255, 0.5)'};
  font-size: 24px;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    top: 8px;
    right: 8px;
  }
`;

const TokenLogo = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  position: relative;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const TokenDetails = styled.div`
  flex: 1;
  min-width: 0;
  padding: 0 8px;

  @media (max-width: 768px) {
    width: 100%;
    padding: 0 8px;
    box-sizing: border-box;
  }
`;

const TokenHeader = styled.div.attrs({
  style: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
    flexWrap: 'wrap',
    padding: '0 4px',
  }
})``;

const TokenTitle = styled.h1.attrs({
  style: {
    fontSize: '24px',
    color: '#4ad0ac',
    fontWeight: 'bold',
    margin: 0,
  }
})``;

const TokenSymbol = styled.span.attrs({
  style: {
    fontSize: '22px',
    color: 'rgba(255, 255, 255, 0.6)',
    display: 'block',
    marginTop: '4px',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    lineHeight: '1.4'
  }
})``;

const SocialLinks = styled.div.attrs({
  style: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginLeft: '16px',
  }
})``;

const SocialIcon = styled.a.attrs({
  style: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
  }
})`
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const TokenDescription = styled.p.attrs({
  style: {
    color: 'rgba(255, 255, 255, 0.8)',
    margin: '8px 0',
    textAlign: 'left',
    fontSize: '16px',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    lineHeight: '1.6'
  }
})``;

const CreateDescription = styled.p.attrs({
  style: {
    color: 'rgba(255, 255, 255, 0.8)',
    margin: 0,
    textAlign: 'left',
    fontSize: '16px',
    marginBottom: '4px',
  }
})``;

const PriceText = styled.p.attrs({
  style: {
    color: '#4ad0ac',
    margin: 0,
    textAlign: 'left',
    fontSize: '16px',
    marginBottom: '4px',
  }
})``;

const TokenAddress = styled.div.attrs({
  style: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: "'Courier New', monospace",
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.7)',
    wordBreak: 'break-all',
    flexWrap: 'wrap',
    marginBottom: '4px',
  }
})``;

const CopyButton = styled.button.attrs({
  style: {
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    borderRadius: '4px',
    padding: '4px 8px',
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  }
})`
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const InfoRewardsWrapper = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 24px;
  margin-bottom: 20px;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GameCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  padding: 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const RoudInfoCard = styled.div`
  padding: 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const GameInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin: 16px 0;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const InfoItem = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
`;

const InfoBlock = styled.div`
  text-align: center;
  width: 100%;
`;
const RoundInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  color: rgba(255, 255, 255, 0.8);
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 600px) {
    gap: 8px;
    justify-content: flex-start;
  }
`;
const RoundInfoBlock = styled.div`
  text-align: center;
  flex: 1 1 0;
  min-width: 120px; // 保证最小宽度
  max-width: 160px;
  margin-bottom: 8px;

  @media (max-width: 600px) {
    min-width: 45vw; // 每行最多2个
    max-width: 48vw;
    flex: 1 1 48vw;
    text-align: left;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #4ad0ac;
`;
const RoundInfoLabel = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 4px;
`;
const RoundInfoValue = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #4ad0ac;
`;


// 奖励图表相关样式
const RewardsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 24px;
  width: 100%;
  justify-content: center;
  max-width: 500px;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;
  height: 100%;
`;

const RewardsChartContainer = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  margin: 0;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
  }
`;

const ChartCenterText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 1;
  animation: breathing 2s ease-in-out infinite;

  @keyframes breathing {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.25);
      opacity: 0.8;
    }
    100% {
      transform: translate(-50%, -50%) scale(1.5);
      opacity: 1;
    }
  }

  .title {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 4px;
    color: #ff0000;
  }

  .value {
    font-size: 16px;
    font-weight: 600;
    color: #ff0000;
  }
`;

const RewardsLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: fit-content;
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }

  @media (max-width: 768px) {
    max-height: 120px;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
    gap: 8px;
  font-size: 14px;
  padding: 2px 0;
  white-space: nowrap;

  span {
    min-width: fit-content;
  }
`;

const LegendColor = styled.span<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.color};
  flex-shrink: 0;
`;

// 游戏操作区相关样式
interface GameOperationWrapperProps {
  hasOperationPanel: boolean;
}

const GameOperationWrapper = styled.div<GameOperationWrapperProps>`
  display: grid;
  grid-template-columns: ${props => props.hasOperationPanel ? '2fr 1fr' : '1fr'};
  gap: 24px;
  margin-bottom: 40px;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 24px;
  }
`;

const RoudInfoStatusCard = styled(RoudInfoCard)`
  height: 100%;
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    margin-bottom: 0; // 可选：如需更大间距可设为 margin-bottom: 20px;
  }
`;

const GameStatusCard = styled(GameCard)`
  height: 100%;
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    margin-bottom: 0; // 可选：如需更大间距可设为 margin-bottom: 20px;
  }
`;

const OperationPanel = styled(GameCard)`
  height: 100%;
  position: sticky;
  top: 24px;
  align-self: stretch;

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    margin-top: 20px;  // 新增：移动端时增加顶部间距
    height: auto;
  }

  @media (min-width: 1024px) {
    position: sticky;
    top: 24px;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
  margin-top: 5px;
`;

const Input = styled.input.attrs({
  style: {
    width: '70%',
    padding: '8px',
    background: 'rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: 'white',
    fontSize: '16px',
    marginRight: '10px',
    outline: 'none',
    transition: 'all 0.3s ease',
  }
  })`
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    
    &[type=number] {
      -moz-appearance: textfield;
    }

    &:focus {
      border-color: #4ad0ac;
      box-shadow: 0 0 0 2px rgba(24, 225, 171, 0.2);
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  `;

const StatusBadge = styled.span<{ status: string }>`
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  background: ${props => {
    switch (props.status) {
      case 'participating':
        return 'linear-gradient(45deg, #4CAF50, #8BC34A)';
      case 'waiting':
        return 'linear-gradient(45deg, #FFC107, #FF9800)';
      case 'burning':
        return 'linear-gradient(45deg, #F44336, #FF5722)';
      case 'claiming':
        return 'linear-gradient(45deg, #2196F3, #03A9F4)';
      default:
        return 'linear-gradient(45deg, #9E9E9E, #757575)';
    }
  }};
  color: ${props => props.status === 'waiting' ? '#333' : 'white'};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin: 16px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: rgba(74, 208, 172, 0.1);
  border-radius: 12px;
  padding: 16px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;
const MintBurnCard = styled.div`
  border-radius: 12px;
  padding: 16px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #4ad0ac;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
`;

const ProgressContainer = styled.div`
  margin: 5px 0;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
  color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
`;

const Timer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  color: rgba(255, 255, 255, 0.8);
`;

const CountdownValue = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 20px;
  font-weight: 700;
  color: #4ad0ac;
`;

// 轮次历史相关样式
const RoundCard = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  margin-bottom: 12px;

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const RoundHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
`;

const RoundTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const RoundDetail = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  padding: 12px;
  background: rgba(13, 17, 28, 0.9);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const DetailTitle = styled.h4`
  font-size: 20px;
  font-weight: 500;
  text-align: left;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 6px;
`;
const TableScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    /* 增加底部内边距，避免横向滚动条被遮挡 */
    padding-bottom: 8px;
  }

  /* 可选：美化滚动条 */
  &::-webkit-scrollbar {
    height: 6px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #222;
    border-radius: 3px;
  }
`;
const RankTable = styled.table`
  width: 100%;
  min-width: 480px;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 6px;

  th {
    padding: 12px 16px;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 500;
    font-size: 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  td {
    padding: 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    font-size: 15px;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const UserDataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 32px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const DataItem = styled.div`
  .label {
  font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 8px;
  }
  .value {
    font-size: 16px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
  }
`;

const RoundStatus = styled.span<{ status: 'active' | 'completed' | 'upcoming' }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'active':
        return 'linear-gradient(45deg, #4CAF50, #8BC34A)';
      case 'completed':
        return 'linear-gradient(45deg, #9E9E9E, #757575)';
      case 'upcoming':
        return 'linear-gradient(45deg, #FF9800, #FFC107)';
      default:
        return '#9E9E9E';
    }
  }};
  color: ${props => props.status === 'upcoming' ? '#333' : 'white'};
`;

const ToggleButton = styled.button<{ isOpen: boolean }>`
  background: none;
  border: none;
  color: #4ad0ac;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(74, 208, 172, 0.1);
  }

  i {
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;
const ShareButton = styled.button`
  background: none;
  border: 1px solid #4ad0ac;
  color: #4ad0ac;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: rgba(74, 208, 172, 0.1);
  }
`;
const BurnButton = styled.button`
  background: none;
  border: 1px solid #F44336;
  color: #F44336;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: rgba(74, 208, 172, 0.1);
  }
`;
const ClaimedButton = styled.button`
  background: none;
  border: 1px solid #4ad0ac;
  color: #4ad0ac;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:disabled {
    border: 1px solid rgb(144, 188, 176);
    color:rgb(144, 188, 176);
    cursor: not-allowed;
    background: rgba(144, 188, 176, 0.08);
  }

`;
const RefundButton = styled.button`
  background: none;
  border: 1px solid #4ad0ac;
  color: #4ad0ac;
  padding: 8px 16px;
  margin-top: 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: rgba(74, 208, 172, 0.1);
  }
`;
interface FirstRoundInfo {
  totalParticipants: string;
  totalDeposit: string;
  totalBurned: string;
  rewardPool: string;
  startTime: string;
  mintEndedTime: string;
  burnEndTime: string;
  isClaimable: boolean;
  isRoundEnded: boolean;
  isSkipped: boolean;
  isFailed: boolean;
  totalBurners: string;
}
interface CurrentRoundInfo {
  roundId: string;
  phase: string;
  currentTime: string;
  totalParticipants: string;
  totalETH: string;
  totalBurned: string;
  rewardPool: string;
  isLastRound: boolean;
  startTime: string;
  endTime: string;
  burnEndTime: string;
  totalBurners: string;
}

interface RoundInfo {
  id: number;
  status: string;
  totalParticipants: string;
  totalDeposit: string;
  totalBurned: string;
  rewardPool: string;
  startTime: string;
  mintEndedTime: string;
  burnEndTime: string;
  isClaimable: boolean;
  isRoundEnded: boolean;
  isSkipped: boolean;
  totalBurners: string;
}

interface GameData {
  gameCreator: string;
  tgLink: string;
  webLink: string;
  xLink: string;
  tokenName: string;
  tokenSymbol: string;
  description: string;
  logoUrl: string;
  contract: string;
  createdAt: string;
  rewardRanks: number[];
  rollOverRate: number;
  totalRewardPool: number;
  totalBurn: string;
  stats: {
    totalSupply: string;
    totalBurned: string;
    totalVolume: string;
    burnRate: string;
  };
  currentRound: number;
  roundDuration: number;
  burnDuration: number;
  totalRounds: number;
  totalDeposits: string;
  rounds: RoundInfo[];
  firstRoundInfo: FirstRoundInfo | null;
  currentRoundInfo: CurrentRoundInfo | null;
  minCap: string;
}

interface RewardsDataType {
  labels: string[];
  datasets: {
    data: number[];
    backgroundColor: string[];
    borderWidth: number;
  }[];
}

interface GameTestProps {
  gameData?: GameData;
  rewardsData?: RewardsDataType;
  loading?: boolean;
}

// 添加一个获取 TotalSupply 的工具函数
const getTotalSupply = async (
  tokenContract: ethers.Contract,
  source: 'contract' | 'graph' = 'contract'
): Promise<string> => {
  try {
    if (source === 'contract') {
      const totalSupply = await tokenContract.totalSupply();
      return ethers.formatUnits(totalSupply, 18);
    } else if (source === 'graph') {
      // TODO: 后期添加从 graph 获取数据的逻辑
      return '0';
    }
    return '0';
  } catch (error) {
    console.error('Error fetching total supply:', error);
    return '0';
  }
};

// 阶段映射
const PHASE_MAP: Record<number, string> = {
  0: 'NotExist',
  1: 'NotStarted',
  2: 'Failure',
  3: 'Participating',
  4: 'WaitingForBurn',
  5: 'Burning',
  6: 'Claiming',
  7: 'Ended',
  8: 'Failed',
};

interface RewardInfo {
  rank: number;
  amount: string;
  isRanked: boolean;
  hasBurned: boolean;
  burnAmount: string;
  roundDeposit: string;
  hasClaimedReward: boolean;
}

const GameTest: React.FC<GameTestProps> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const chainId = useChainId();
  const nativeToken = getNativeToken(chainId);
  const tradeUrl = getTradeUrl(chainId);
  const { isConnected, address } = useAccount();
  const signer = useEthersSigner();
  const [isLoading, setIsLoading] = useState(false); 
  const gameAddress = searchParams.get('address');
  const [tokenAddress, setTokenAddress] = useState<string>(location.state?.tokenAddress);
  const [loading, setLoading] = useState(true);
  const publicClient = usePublicClient();
  const [failedImage, setFailedImage] = useState(false);
  const [gameStatus, setGameStatus] = useState<'notexist' | 'notstarted' | 'failed' | 'participating' | 'waiting' | 'burning' | 'claiming' | 'ended'>('participating');
  const [currentPrice, setCurrentPrice] = useState<string>('0');
  const [canRefund, setCanRefund] = useState<boolean>(false);
  const [refundAmount, setRefundAmount] = useState<string>('0');
  const [mintAmount, setMintAmount] = useState<string>('');
  const [burnAmount, setBurnAmount] = useState<string>('');
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [holdingAmount, setHoldingAmount] = useState<string>('0');
  const { openConnectModal } = useConnectModal();
  const [gameData, setGameData] = useState<GameData>({
    gameCreator: '',
    tgLink: '',
    webLink: '',
    xLink: '',
    tokenName: '',
    tokenSymbol: '',
    description: '',
    logoUrl: '',
    contract: '',
    createdAt: '',
    rewardRanks: [],
    rollOverRate: 0,
    totalRewardPool: 0,
    totalBurn: '',
    stats: {
      totalSupply: '0',
      totalBurned: '0',
      totalVolume: '0',
      burnRate: '0'
    },
    currentRound: 1,
    totalRounds: 0,
    roundDuration: 0,
    burnDuration: 0,
    totalDeposits: '0',
    rounds: [],
    firstRoundInfo: null,
    currentRoundInfo: null,
    minCap: '0'
  });

  // 定义颜色数组
  const colorPalette = [
    '#E91E63', // 粉色
    '#FF5722', // 橙色
    '#4CAF50', // 绿色
    '#2196F3', // 蓝色
    '#FFC107', // 黄色
    '#9C27B0', // 紫色
    '#00BCD4', // 青色
    '#3F51B5', // 靛蓝
    '#009688', //  teal
    '#673AB7', // 深紫色
    '#795548', // 棕色
  ];

  // 生成标签和背景色的函数
  const generateRewardsData = (data: number[]) => {
    // 分离 Others 数据
    const othersValue = data[data.length - 1];
    const mainData = data.slice(0, -1);

    // 生成主要数据的标签
    const labels = mainData.map((_, index) => {
      if (index === 0) return '1st';
      if (index === 1) return '2nd';
      if (index === 2) return '3rd';
      return `${index + 1}th`;
    });
    // 添加 Others 标签
    labels.push('Others');
    // 生成主要数据的背景色
    const backgroundColor = mainData.map((_, index) => colorPalette[index % colorPalette.length]);
    // 添加 Others 的背景色
    backgroundColor.push('#757575');

    return {
      labels,
      datasets: [{
        data: [...mainData, othersValue],
        backgroundColor,
        borderWidth: 0
      }]
    };
  };

  const [rewardsData, setRewardsData] = useState<RewardsDataType>(() => {
    // 示例数据，实际使用时可以从API获取
    const data = [0];
    return generateRewardsData(data);
  });

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    animation: {
      duration: 750,
      easing: 'easeInOutQuart' as const
    },
    hover: {
      mode: undefined as any
    },
    plugins: {
      legend: {
        display: false
      }
    },
    cutout: '70%',
    elements: {
      arc: {
        borderWidth: 0
      }
    }
  }), []);

  // 在 GameTest 组件内添加 state
  const [openRoundDetails, setOpenRoundDetails] = useState<number[]>([]);
  // 添加获奖者数据的状态
  const [winnersData, setWinnersData] = useState<Record<number, {
    winners: string[];
    rewards: string[];
    burnAmounts: string[];
  }>>({});

  const [userRewardData, setUserRewardData] = useState<Record<number, RewardInfo>>({});

  // 添加获取轮次详情的函数
  const fetchRoundDetails = useCallback(async (roundId: number) => {
    try {
      if (!gameAddress || !publicClient?.transport?.url) return;
      
      const provider = new ethers.JsonRpcProvider(publicClient.transport.url);
      const gameContract = new ethers.Contract(
        gameAddress,
        YOLOGame.abi,
        provider
      );

      // 获取获奖者数据
      const winnersResult = await gameContract.getAllWinners(roundId);
      const [winners, rewards, burnAmounts] = winnersResult;
      
      // 检查是否有数据
      if (winners && winners.length > 0) {
        setWinnersData(prev => ({
          ...prev,
          [roundId]: {
            winners: winners.map((addr: string) => addr),
            rewards: rewards.map((amount: bigint) => ethers.formatEther(amount)),
            burnAmounts: burnAmounts.map((amount: bigint) => ethers.formatEther(amount))
          }
        }));
      } else {
        // 如果没有数据，检查是否已经有缓存的数据
        setWinnersData(prev => {
          // 如果已经有缓存的数据，保持不变
          if (prev[roundId] && prev[roundId].winners.length > 0) {
            return prev;
          }
          // 如果没有缓存的数据，设置空数组
          return {
            ...prev,
            [roundId]: {
              winners: [],
              rewards: [],
              burnAmounts: []
            }
          };
        });
      }

      // 如果有用户地址，获取用户奖励数据
      if (address) {
        const userRewardResult = await gameContract.getRewardInfo(roundId, address);
        setUserRewardData(prev => ({
          ...prev,
          [roundId]: {
            rank: Number(userRewardResult.rank),
            amount: ethers.formatEther(userRewardResult.amount),
            isRanked: userRewardResult.isRanked,
            hasBurned: userRewardResult.hasBurned,
            burnAmount: ethers.formatEther(userRewardResult.burnAmount),
            roundDeposit: ethers.formatEther(userRewardResult.roundDeposit),
            hasClaimedReward: userRewardResult.hasClaimedReward
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching round details:', error);
      // 发生错误时，检查是否已经有缓存的数据
      setWinnersData(prev => {
        // 如果已经有缓存的数据，保持不变
        if (prev[roundId] && prev[roundId].winners.length > 0) {
          return prev;
        }
        // 如果没有缓存的数据，设置空数组
        return {
          ...prev,
          [roundId]: {
            winners: [],
            rewards: [],
            burnAmounts: []
          }
        };
      });
    }
  }, [gameAddress, publicClient, address]);

  const toggleRoundDetail = async (roundId: number) => {
    // 如果已经打开，则关闭
    if (openRoundDetails.includes(roundId)) {
      setOpenRoundDetails(prev => prev.filter(id => id !== roundId));
      return;
    }

    // 如果还没有获取过该轮次的获奖者数据，则获取
    if (!winnersData[roundId]) {
      await fetchRoundDetails(roundId);
    }

    // 打开详情
    setOpenRoundDetails(prev => [...prev, roundId]);
  };
  // 添加错误状态
  const [error, setError] = useState<string | null>(null);

  // 优化事件处理函数
  const handleConnectWallet = useCallback(() => {
  }, []);

  const { showToast } = useToast();

  const handleMint = useCallback(async () => {
    if (!gameAddress) {
      showToast('Game address is not valid', 'error');
      return;
    }
    
    if (!isConnected || !address || !signer) {
      openConnectModal?.();
      return;
    }
    
    try {
      const depositAmount = ethers.parseEther(mintAmount);
      setIsLoading(true);
      const contract = new ethers.Contract(
        gameAddress,
        ['function deposit() external'],
        signer
      );
      const tx = await contract.deposit({ value: depositAmount });
      await tx.wait();
      setIsLoading(false);
      showToast('Mint success', 'success');
      setMintAmount('0');
    } catch (error) {
      setIsLoading(false);
      console.error('Error minting:', error);
      showToast('Failed to mint: ' + (error as Error).message, 'error');
    }
  }, [isConnected, address, signer, gameAddress, mintAmount, showToast]);

  const handleBurn = useCallback(async () => {
    if (!gameAddress) {
      showToast('Game address is not valid', 'error');
      return;
    }
    
    if (!isConnected || !address || !signer) {
      openConnectModal?.();
      return;
    }
    
    try {
      const burnAmountToken = ethers.parseEther(burnAmount);
      if (BigInt(holdingAmount) < burnAmountToken) {
        showToast('Insufficient balance', 'error');
        return;
      }

      const tokenContract = new ethers.Contract(
        tokenAddress,
        YOLOToken.abi,
        signer
      );
      setIsLoading(true);
      const allowance = await tokenContract.allowance(address, gameAddress);
      if(allowance < burnAmountToken){
        const tx = await tokenContract.approve(gameAddress, burnAmountToken);
        await tx.wait();
      }

      const contract = new ethers.Contract(
        gameAddress,
        ['function burnToCompete(uint256 amount) external'],
        signer
      );
      const tx = await contract.burnToCompete(burnAmountToken);
      await tx.wait();
      setIsLoading(false);
      showToast('Burn success', 'success');
      setBurnAmount('0');
    } catch (error) {
      setIsLoading(false);
      console.error('Error minting:', error);
      showToast('Failed to mint: ' + (error as Error).message, 'error');
    }
  }, [isConnected, address, signer, gameAddress, burnAmount, showToast, holdingAmount]);

  const handleBuyToken = useCallback(() => {
    navigate('/create');
  }, [navigate]);

  const handleCopyAddress = useCallback((address: string) => {
    if (!address) return;
    
    // 检查clipboard API是否可用
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(address)
        .catch(err => {
          console.error('Failed to copy:', err);
          fallbackCopy(address);
        });
    } else {
      fallbackCopy(address);
    }
    showToast('Copied', 'success');
  }, []);

  // 备选复制方案
  const fallbackCopy = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      textArea.remove();
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
      textArea.remove();
    }
  };

  const handleImageError = useCallback(() => {
    setFailedImage(true);
  }, []);

  // 优化格式化函数
  const formatAddress = useCallback((address: string | null) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  }, []);

  const renderImage = useMemo(() => {
    if (!gameData.logoUrl || failedImage) {
      return <span>{gameData.tokenSymbol?.slice(0, 2) || 'YP'}</span>;
    }
    return (
      <img 
        src={gameData.logoUrl}
        alt={gameData.tokenName}
        onError={() => setFailedImage(true)}
        loading="lazy"
      />
    );
  }, [gameData.logoUrl, gameData.tokenName, gameData.tokenSymbol, failedImage]);

  const renderVideo = useMemo(() => {
    if (!gameData.logoUrl) return null;
    return (
      <video
        src={gameData.logoUrl}
        autoPlay
        loop
        muted
        playsInline
        onError={() => setFailedImage(true)}
      />
    );
  }, [gameData.logoUrl]);

  const progress = gameData.firstRoundInfo ? (parseFloat(ethers.formatEther(gameData.firstRoundInfo.totalDeposit)) / parseFloat(gameData.minCap)) * 100 : 0;

  // 当前轮进度条与倒计时逻辑
  const [roundProgress, setRoundProgress] = useState({
    progress: 0,
    show: false,
    label: '',
    countdownStr: '',
  });
  const fetchCurrentPrice = useCallback(async () => {
    if (!tokenAddress || !publicClient?.transport?.url) return;
    try {
      const provider = new ethers.JsonRpcProvider(publicClient.transport.url);
      const tokenContract = new ethers.Contract(
        tokenAddress,
        YOLOToken.abi,
        provider
      );
      const price = await tokenContract.getCurrentPrice();
      setCurrentPrice(ethers.formatEther(price));
    } catch (error) {
      console.error('Error fetching price:', error);
    }
  }, [tokenAddress, publicClient]);

  useEffect(() => {
    // 只在数据加载完成后再执行
    if (loading || !gameData.currentRoundInfo) return;
    let timer: NodeJS.Timeout | null = null;
    const phaseNum = Number(gameData.currentRoundInfo.phase);
    const phase = PHASE_MAP[phaseNum] || gameData.currentRoundInfo.phase;
    
    let start = 0, end = 0, now = 0, label = '', show = false;

    now = Number(gameData.currentRoundInfo.currentTime);
    if(gameData.firstRoundInfo?.isFailed){
      setRoundProgress({ progress: 0, show: false, label: 'Failed', countdownStr: '' });
      return;
    } else if (phase === 'Participating') {
      start = Number(gameData.currentRoundInfo.startTime);
      end = Number(gameData.currentRoundInfo.endTime);
      label = 'Participating';
      show = true;
    } else if (phase === 'Burning') {
      start = Number(gameData.currentRoundInfo.endTime);
      end = Number(gameData.currentRoundInfo.burnEndTime);
      label = 'Burning';
      show = true;
    } else if (phase === 'NotStarted') {
      start = Number(gameData.currentRoundInfo.startTime);
      end = Number(gameData.currentRoundInfo.endTime);
      label = 'Participating';
      show = true;
    } else {
      setRoundProgress({ progress: 0, show: false, label: '', countdownStr: '' });
      return;
    }

    const total = end - start;
    let left = Math.max(end - now, 0);
    let done = now - start;
    const format = (sec: number) => {
      const h = String(Math.floor(sec / 3600)).padStart(2, '0');
      const m = String(Math.floor((sec % 3600) / 60)).padStart(2, '0');
      const s = String(sec % 60).padStart(2, '0');
      return `${h}:${m}:${s}`;
    };

    // 计算初始进度
    const initialProgress = total > 0 ? Math.min((done / total) * 100, 100) : 0;
    setRoundProgress({
      progress: initialProgress,
      show,
      label,
      countdownStr: format(left),
    });

    // 每秒更新进度和倒计时
    timer = setInterval(() => {
      left = Math.max(left - 1, 0);
      done = done + 1;
      const currentProgress = total > 0 ? Math.min((done / total) * 100, 100) : 0;
      setRoundProgress(prev => ({
        ...prev,
        countdownStr: format(left),
        progress: currentProgress,
      }));
      if (left <= 0 && timer) {
        clearInterval(timer);
        // 倒计时结束后立即执行一次轮询
        fetchGameInfo();
        
        // 如果是最后一轮的burn状态倒计时结束，则停止轮询
        if (phase === 'Burning' && gameData.currentRound === gameData.totalRounds) {
          setPollingEnabled(false);
        }
      }
    }, 1000);

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [gameData.currentRoundInfo, loading]);


  // 添加 useEffect 来更新饼图数据
  useEffect(() => {
    if (gameData.rewardRanks.length > 0) {
      setRewardsData(generateRewardsData(gameData.rewardRanks));
    }
  }, [gameData.rewardRanks]);

  const handleRefund = useCallback(async () => {
    if (isConnected && address && signer && gameAddress) {    
      try {
        setIsLoading(true);
        const contract = new ethers.Contract(
          gameAddress,
          ['function refund() external'],
          signer
        );

        const tx = await contract.refund();
        await tx.wait();
        setCanRefund(false);
        setRefundAmount('0');
        setIsLoading(false);
        showToast('Refund success', 'success');
      } catch (error) {
        setIsLoading(false);
        console.error('Error refunding:', error);
        showToast('Failed to refund: ' + (error as Error).message, 'error');
      }
    }
  }, [gameAddress, publicClient, address, signer, showToast]);

  // 处理收藏/取消收藏
  const handleToggleFavorite = async () => {
    setIsLoading(true);
    // 这里可以添加收藏/取消收藏的逻辑
    try {
      if(isFavorite){
        // 取消收藏
        const contract = new ethers.Contract(
          getFactoryAddress(chainId),
          ['function removeFavorite(address gameAddress) external'],
          signer
        );
        const tx = await contract.removeFavorite(gameAddress);
        await tx.wait();
      }else{
        // 收藏
        const contract = new ethers.Contract(
          getFactoryAddress(chainId),
          ['function addFavorite(address gameAddress) external'],
          signer
        );
        const tx = await contract.addFavorite(gameAddress);
        await tx.wait();
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }finally{
      setIsLoading(false);
    }
  };

  const [middleRounds, setMiddleRounds] = useState<RoundInfo[]>([]);
  const [isLoadingMiddleRounds, setIsLoadingMiddleRounds] = useState(false);

  // 修改 fetchMiddleRounds 函数
  const fetchMiddleRounds = useCallback(async (gameContract: ethers.Contract, currentRoundId: number) => {
    if (currentRoundId <= 2) return;
    
    setIsLoadingMiddleRounds(true);
    const middleRoundsData: RoundInfo[] = [];
    
    try {
      const batchSize = 5;
      const totalMiddleRounds = currentRoundId - 2;
      
      for (let i = 0; i < totalMiddleRounds; i += batchSize) {
        const batchEnd = Math.min(i + batchSize, totalMiddleRounds);
        const batchPromises = Array.from(
          { length: batchEnd - i },
          (_, index) => gameContract.getRoundInfo(i + index + 2)
        );
        
        const batchResults = await Promise.all(batchPromises);
        middleRoundsData.push(...batchResults.map((info, index) => ({
          id: i + index + 2,
          totalParticipants: info[0].toString(),
          totalDeposit: info[1].toString(),
          totalBurned: info[2].toString(),
          rewardPool: info[3].toString(),
          startTime: info[5].toString(),
          mintEndedTime: info[6].toString(),
          burnEndTime: info[7].toString(),
          isClaimable: info[8],
          isRoundEnded: info[9],
          isSkipped: info[10],
          totalBurners: info[11].toString(),
          status: info[4] ? 'active' : 'completed'
        })));
        
        if (i + batchSize < totalMiddleRounds) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }
      
      setMiddleRounds(middleRoundsData);
    } catch (error) {
      console.error('Error fetching middle rounds:', error);
    } finally {
      setIsLoadingMiddleRounds(false);
    }
  }, []);

  // 修改 useEffect 钩子
  useEffect(() => {
    if (middleRounds.length > 0) {
      setGameData(prev => {
        // 检查当前轮次是否为第一轮
        const isFirstRound = prev.currentRound === 1;
        
        // 如果是第一轮，只保留第一轮数据
        if (isFirstRound) {
          return {
            ...prev,
            rounds: [prev.rounds[0]]
          };
        }
        
        // 如果不是第一轮，则合并第一轮、中间轮和当前轮
        return {
          ...prev,
          rounds: [
            prev.rounds[0],
            ...middleRounds,
            prev.rounds[prev.rounds.length - 1]
          ]
        };
      });
    }
  }, [middleRounds]);

  // 保留这个完整的 fetchGameInfo 函数
  const POLLING_INTERVAL = 20000; // 20秒
  const [pollingEnabled, setPollingEnabled] = useState(true);

  // 将 fetchGameInfo 包装在 useCallback 中
  const fetchGameInfo = useCallback(async () => {
    if (!gameAddress || !publicClient?.transport?.url) {
      console.log('Missing required data:', { gameAddress, hasPublicClient: !!publicClient });
      setLoading(false);
      return;
    }

    // 检查当前链是否支持该游戏
    const factoryAddress = getFactoryAddress(chainId);
    if (!factoryAddress) {
      setError('Contract not deployed on current network');
        setLoading(false);
        return;
      }

      try {
        const provider = new ethers.JsonRpcProvider(publicClient.transport.url);
        
        const gameContract = new ethers.Contract(
          gameAddress,
          YOLOGame.abi,
          provider
        );
      const gameInfo = await gameContract.getGameInfo();

      let currentTokenAddress = tokenAddress;
      if (!currentTokenAddress) {
        currentTokenAddress = gameInfo.tokenAddress;
        setTokenAddress(currentTokenAddress);
      }
      
      if (!currentTokenAddress) {
        throw new Error('Failed to get token address');
      }
        
        const tokenContract = new ethers.Contract(
        currentTokenAddress,
          YOLOToken.abi,
          provider
        );

      const [tokenInfo, firstRoundInfo, currentRoundInfo, totalDeposits, totalSupply] = await Promise.all([
          tokenContract.getTokenInfo(),
          gameContract.getRoundInfo(1),
        gameContract.getCurrentRoundStatus(),
        gameContract.totalDeposits(),
        getTotalSupply(tokenContract, 'contract')
      ]);
      //  // 解构你需要的字段
      //  const {
      //   creator,
      //   creatTime,
      //   minCapETH,
      //   roundDuration,
      //   burnDuration,
      //   totalRounds,
      //   totalRewardPool,
      //   rewardRanks,
      //   rollOverRate,
      //   totalBurn,
      //   name,
      //   symbol,
      //   description,
      //   logoUrl,
      //   gTokenAddress,
      //   gCurrentTime,
      //   gCurrentRoundId
      // } = gameInfo;



      // 如果有用户地址，获取用户相关的数据（这部分不需要轮询）
      let isFavorited = false;
      let holdingAmount = '0';
      if (address) {
        const factoryContract = new ethers.Contract(
          getFactoryAddress(chainId),
          YOLOFactory.abi,
          provider
        );
        [isFavorited, holdingAmount] = await Promise.all([
          factoryContract.isGameFavorited(address, gameAddress),
          tokenContract.balanceOf(address)
        ]);
        setIsFavorite(isFavorited);
        setHoldingAmount(holdingAmount);
      }

      const tokenSymbol = tokenInfo[1];
        const totalBurned = ethers.formatEther(firstRoundInfo[2].toString());
        const totalDeposit = ethers.formatEther(firstRoundInfo[1].toString());
      const burnRate = totalDeposit === '0' ? '0' : ((parseFloat(totalBurned) / parseFloat(totalDeposit)) * 100).toFixed(2);
      const currentTime = currentRoundInfo[2];
      const totalRound = Number(gameInfo.totalRounds);
      let currentRoundId = Number(currentRoundInfo[0]);
      let roundInfo = [...currentRoundInfo];
      
      // 如果当前轮次大于总轮次，需要重新获取最后一轮的信息
      if (currentRoundId > totalRound) {
        setPollingEnabled(false);
        currentRoundId = totalRound;
        let finalCurrentRoundInfo = await gameContract.getRoundInfo(totalRound);
        roundInfo = [
          currentRoundId,
          roundInfo[1],
          roundInfo[2],
          finalCurrentRoundInfo[0].toString(),
          finalCurrentRoundInfo[1],
          finalCurrentRoundInfo[2].toString(),
          finalCurrentRoundInfo[3].toString(),
          true,
          finalCurrentRoundInfo[5].toString(),
          finalCurrentRoundInfo[6],
          finalCurrentRoundInfo[7],
          finalCurrentRoundInfo[11].toString()
        ];
      }

      const isFailed = currentTime > firstRoundInfo[6] ? firstRoundInfo[1] < gameInfo.minCapETH : false;
      if(isFailed) {
        setPollingEnabled(false);
      }
      // 设置初始轮次数据
      const initialRounds = isFailed ? [
        {
          id: 1,
          totalParticipants: firstRoundInfo[0].toString(),
          totalDeposit: firstRoundInfo[1].toString(),
          totalBurned: firstRoundInfo[2].toString(),
          rewardPool: firstRoundInfo[3].toString(),
          startTime: firstRoundInfo[5].toString(),
          mintEndedTime: firstRoundInfo[6].toString(),
          burnEndTime: firstRoundInfo[7].toString(),
          isClaimable: firstRoundInfo[8],
          isRoundEnded: firstRoundInfo[9],
          isSkipped: firstRoundInfo[10],
          totalBurners: firstRoundInfo[11].toString(),
          status: 'completed'
        }
      ] : currentRoundId === 1 ? [
        {
          id: 1,
          totalParticipants: firstRoundInfo[0].toString(),
          totalDeposit: firstRoundInfo[1].toString(),
          totalBurned: firstRoundInfo[2].toString(),
          rewardPool: firstRoundInfo[3].toString(),
          startTime: firstRoundInfo[5].toString(),
          mintEndedTime: firstRoundInfo[6].toString(),
          burnEndTime: firstRoundInfo[7].toString(),
          isClaimable: firstRoundInfo[8],
          isRoundEnded: firstRoundInfo[9],
          isSkipped: firstRoundInfo[10],
          totalBurners: firstRoundInfo[11].toString(),
          status: firstRoundInfo[4] ? 'active' : (firstRoundInfo[9] ? 'completed' : 'upcoming')
        }
      ] : [
        {
          id: 1,
          totalParticipants: firstRoundInfo[0].toString(),
          totalDeposit: firstRoundInfo[1].toString(),
          totalBurned: firstRoundInfo[2].toString(),
          rewardPool: firstRoundInfo[3].toString(),
          startTime: firstRoundInfo[5].toString(),
          mintEndedTime: firstRoundInfo[6].toString(),
          burnEndTime: firstRoundInfo[7].toString(),
          isClaimable: firstRoundInfo[8],
          isRoundEnded: firstRoundInfo[9],
          isSkipped: firstRoundInfo[10],
          totalBurners: firstRoundInfo[11].toString(),
          status: firstRoundInfo[4] ? 'active' : (firstRoundInfo[9] ? 'completed' : 'upcoming')
        },
        {
          id: currentRoundId,
          totalParticipants: roundInfo[3],
          totalDeposit: roundInfo[4],
          totalBurned: roundInfo[5],
          rewardPool: roundInfo[6],
          startTime: roundInfo[8],
          mintEndedTime: roundInfo[9],
          burnEndTime: roundInfo[10],
          totalBurners: roundInfo[11].toString(),
          isClaimable: false,
          isRoundEnded: currentRoundId === totalRound && Number(roundInfo[2]) > Number(roundInfo[10]),
          isSkipped: false,
          status: currentRoundId === totalRound && Number(roundInfo[2]) > Number(roundInfo[10]) ? 'completed' : 'active'
        }
      ];

        setGameData({
        gameCreator: gameInfo.creator,
          tokenName: tokenInfo[0],
        tokenSymbol: tokenSymbol,
          description: tokenInfo[2],
        logoUrl: tokenInfo[3],
        tgLink: tokenInfo[4],
        webLink: tokenInfo[5],
        xLink: tokenInfo[6],
          contract: gameAddress,
        createdAt: new Date(Number(firstRoundInfo[5]) * 1000).toLocaleString(),
        rewardRanks: (() => {
          const ranks = gameInfo.rewardRanks.map((rank: bigint) => Number(ethers.formatUnits(rank, 0)) / 100);
          const currentSum = ranks.reduce((sum: number, rank: number) => sum + rank, 0);
          return [...ranks, 100 - currentSum];
        })(),
        rollOverRate: Number(gameInfo.rollOverRate) / 100,
        totalRewardPool: gameInfo.totalRewardPool,
        totalBurn: ethers.formatUnits(gameInfo.totalBurn, 18),
          stats: {
          totalSupply: `${totalSupply}`,
          totalBurned: `${ethers.formatEther(currentRoundInfo[5].toString())} ${nativeToken}`,
          totalVolume: `${ethers.formatEther(currentRoundInfo[4].toString())} ${nativeToken}`,
            burnRate: `${burnRate}%`
          },
        currentRound: isFailed ? 1 : currentRoundId,
        roundDuration: gameInfo.roundDuration,
        burnDuration: gameInfo.burnDuration,
        totalRounds: gameInfo.totalRounds,
        totalDeposits: totalDeposits,
        rounds: initialRounds,
          firstRoundInfo: {
            totalParticipants: firstRoundInfo[0].toString(),
            totalDeposit: firstRoundInfo[1].toString(),
            totalBurned: firstRoundInfo[2].toString(),
            rewardPool: firstRoundInfo[3].toString(),
            startTime: firstRoundInfo[5].toString(),
            mintEndedTime: firstRoundInfo[6].toString(),
            burnEndTime: firstRoundInfo[7].toString(),
            isClaimable: firstRoundInfo[8],
            isRoundEnded: firstRoundInfo[9],
            isSkipped: firstRoundInfo[10],
            isFailed: isFailed,
            totalBurners: firstRoundInfo[11].toString()
        },
        currentRoundInfo: {
          roundId: isFailed ? 1 : currentRoundInfo[0].toString(),
          phase: isFailed ? 'Failed' : currentRoundInfo[1],
          currentTime: isFailed ? 0 : currentRoundInfo[2].toString(),
          totalParticipants: isFailed ? firstRoundInfo[0].toString() : currentRoundInfo[3].toString(),
          totalETH: isFailed ? firstRoundInfo[1].toString() : currentRoundInfo[4].toString(),
          totalBurned: isFailed ? firstRoundInfo[2].toString() : currentRoundInfo[5].toString(),
          rewardPool: isFailed ? firstRoundInfo[3].toString() : currentRoundInfo[6].toString(),
          isLastRound: isFailed ? true : currentRoundInfo[7],
          startTime: isFailed ? firstRoundInfo[5].toString() : currentRoundInfo[8].toString(),
          endTime: isFailed ? firstRoundInfo[6].toString() : currentRoundInfo[9].toString(),
          burnEndTime: isFailed ? firstRoundInfo[7].toString() : currentRoundInfo[10].toString(),
          totalBurners: isFailed ? firstRoundInfo[11].toString() : currentRoundInfo[11].toString()
        },
        minCap: ethers.formatEther(gameInfo.minCapETH)
      });

      if (!isFailed && currentRoundId > 2) {
        fetchMiddleRounds(gameContract, currentRoundId);
      }

      const mappedStatus = PHASE_MAP[currentRoundInfo[1]];
      const newStatus = isFailed ? 'failed' : mappedStatus.toLowerCase() as 'notexist' | 'notstarted' | 'failed' | 'participating' | 'waiting' | 'burning' | 'claiming' | 'ended';
      setGameStatus(newStatus);
      setError(null);

      if(isFailed) {
        try {
          const checkRefundState = await gameContract.checkRefundState(address);
          setCanRefund(checkRefundState[0]);
          setRefundAmount(ethers.formatEther(checkRefundState[1]));
        } catch(error) {
          console.error('Error fetching refund state:', error);
        }
      }

      setLoading(false);
      } catch (error) {
      console.error('Error in fetchGameInfo:', error);
      setError('Failed to load game data. Please ensure you are on the correct network');
        setLoading(false);
      }
  }, [gameAddress, publicClient, tokenAddress, address, nativeToken, fetchMiddleRounds, chainId]);

  // 设置轮询和初始加载
  useEffect(() => {
    
    // 立即执行一次初始加载
    fetchGameInfo();
    fetchCurrentPrice();

    // 如果不需要轮询，直接返回
    if (!pollingEnabled) return;

    // 设置定时器进行轮询
    const timer = setInterval(() => {
      fetchGameInfo();
      fetchCurrentPrice();
      fetchRoundDetails(gameData.currentRound);
    }, POLLING_INTERVAL);

    // 清理函数
    return () => {
      clearInterval(timer);
    };
  }, [pollingEnabled, fetchGameInfo,fetchCurrentPrice, gameData.firstRoundInfo?.isFailed]);

  // 在组件顶部添加 handleClaim 函数
  const handleClaim = useCallback(async (roundId: number) => {
    if (!gameAddress || !isConnected || !address || !signer) {
      openConnectModal?.();
      return;
    }

    try {
      setIsLoading(true);
      const contract = new ethers.Contract(
        gameAddress,
        ['function claimReward(uint256 roundId) external'],
        signer
      );
      const tx = await contract.claimReward(roundId);
      await tx.wait();
      setIsLoading(false);
      showToast('Claim success', 'success');
      // 只更新本地状态
      setUserRewardData(prev => ({
        ...prev,
        [roundId]: {
          ...prev[roundId],
          hasClaimedReward: true
        }
      }));
    } catch (error) {
      setIsLoading(false);
      console.error('Error claiming reward:', error);
      showToast('Failed to claim: ' + (error as Error).message, 'error');
    }
  }, [gameAddress, isConnected, address, signer, showToast]);

  if (error) {
    return (
      <>
        <Sidebar />
        <GameContainer>
          <GameContent>
            <div className="text-red-500">{error}</div>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
            >
              Retry
            </button>
          </GameContent>
        </GameContainer>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Sidebar />
        <GameContainer>
          <GameContent>
            <Loading />
          </GameContent>
        </GameContainer>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <GameContainer>
        <HomeTopButtons 
          onFirstButtonClick={handleConnectWallet}
          onSecondButtonClick={handleBuyToken}
        />
        
        <GameContent>
          {/* Token Info Section */}
          <TokenInfoSection>
            <FavoriteIcon 
              isFavorite={isFavorite} 
              onClick={handleToggleFavorite}
              aria-label={isFavorite ? "cancel favorite" : "favorite"}
            >
              {isFavorite ? '★' : '☆'}
            </FavoriteIcon>
            <TokenLogo>
              {failedImage ? renderVideo : renderImage}
            </TokenLogo>
            <TokenDetails>
              <TokenHeader>
                <TokenTitle>{gameData.tokenSymbol}</TokenTitle>
                <TokenSymbol>
                {gameData.tokenName && gameData.tokenName.length > 100 
                      ? `${gameData.tokenName.slice(0, 100)}...` 
                      : gameData.tokenName}</TokenSymbol>
                <SocialLinks>
                  {gameData.tgLink && (
                    <SocialIcon 
                      href={gameData.tgLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      title="Telegram"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.324-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.119.098.152.228.166.331.014.103.027.325.016.503z"/>
                      </svg>
                    </SocialIcon>
                  )}
                  {gameData.webLink && (
                    <SocialIcon 
                      href={gameData.webLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      title="Website"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm1 16.057v-3.057h2.994c-.059 1.143-.212 2.24-.456 3.279-.823-.12-1.674-.188-2.538-.222zm1.957 2.162c-.499 1.33-1.159 2.497-1.957 3.456v-3.62c.666.028 1.319.081 1.957.164zm-1.957-7.219v-3.015c.868-.034 1.721-.103 2.548-.224.238 1.027.389 2.111.446 3.239h-2.994zm0-5.014v-3.661c.806.969 1.471 2.15 1.971 3.496-.642.084-1.3.137-1.971.165zm2.703-3.267c1.237.496 2.354 1.228 3.29 2.146-.642.234-1.311.442-2.019.607-.344-.992-.775-1.91-1.271-2.753zm-7.241 13.56c-.244-1.039-.398-2.136-.456-3.279h2.994v3.057c-.865.034-1.714.102-2.538.222zm2.538 1.776v3.62c-.798-.959-1.458-2.126-1.957-3.456.638-.083 1.291-.136 1.957-.164zm-2.994-7.055c.057-1.128.207-2.212.446-3.239.827.121 1.68.19 2.548.224v3.015h-2.994zm1.024-5.179c.5-1.346 1.165-2.527 1.97-3.496v3.661c-.671-.028-1.329-.081-1.97-.165zm-2.005-.35c-.708-.165-1.377-.373-2.018-.607.937-.918 2.053-1.65 3.29-2.146-.496.844-.927 1.762-1.272 2.753zm-.549 1.918c-.264 1.151-.434 2.36-.492 3.611h-3.933c.165-1.658.739-3.197 1.617-4.518.88.361 1.816.67 2.808.907zm.009 9.262c-.988.236-1.92.542-2.797.9-.89-1.328-1.471-2.879-1.637-4.551h3.934c.058 1.265.231 2.488.5 3.651zm.553 1.917c.342.976.768 1.881 1.257 2.712-1.223-.49-2.326-1.211-3.256-2.115.636-.229 1.299-.435 1.999-.597zm9.924 0c.7.163 1.362.367 1.999.597-.931.903-2.034 1.625-3.257 2.116.489-.832.915-1.737 1.258-2.713zm.553-1.917c.27-1.163.442-2.386.501-3.651h3.934c-.167 1.672-.748 3.223-1.638 4.551-.877-.358-1.81-.664-2.797-.9zm.501-5.651c-.058-1.251-.229-2.46-.492-3.611.992-.237 1.929-.546 2.809-.907.877 1.321 1.451 2.86 1.616 4.518h-3.933z"/>
                      </svg>
                    </SocialIcon>
                  )}
                  {gameData.xLink && (
                    <SocialIcon 
                      href={gameData.xLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      title="X (Twitter)"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </SocialIcon>
                  )}
                </SocialLinks>
              </TokenHeader>
              <TokenDescription>
                {gameData.description && gameData.description.length > 200 
                      ? `${gameData.description.slice(0, 200)}...` 
                      : gameData.description}
              </TokenDescription>
              
              <TokenAddress>
                <CopyButton onClick={() => handleCopyAddress(tokenAddress || '')}>
                  Token:{formatAddress(tokenAddress)} 📋
                </CopyButton>
                <CopyButton onClick={() => handleCopyAddress(gameAddress || '')}>
                  Game:{formatAddress(gameAddress)} 📋
                </CopyButton>
                <CopyButton onClick={() => handleCopyAddress(gameData.gameCreator || '')}>
                  Create By:{formatAddress(gameData.gameCreator)} 📋
                </CopyButton>
              </TokenAddress>
              <CreateDescription>Create At: {gameData.createdAt} TotalSupply: {Number(gameData.stats.totalSupply).toFixed(2)} TotalBurned: {Number(gameData.totalBurn).toFixed(2)}</CreateDescription>
              <PriceText>MintPrice: {Number(currentPrice).toFixed(12)} {nativeToken}
                <a
                  href={tokenAddress ? `${tradeUrl}${tokenAddress}` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginLeft: '10px', verticalAlign: 'middle', display: 'inline-flex', alignItems: 'center' }}
                  title="Trade"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ad0ac" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              </PriceText>

            </TokenDetails>
            
          </TokenInfoSection>

          {/* Info Rewards Section */}
          <InfoRewardsWrapper>
            {/* Game Info Card */}
            <RoudInfoStatusCard>
              <h2 className="text-xl font-bold mb-4">Round Information</h2>
              <div className="flex justify-between items-center mb-6">
                <StatusBadge status={gameStatus}>
                  {gameStatus === 'notexist' ? 'Ended' :
                    gameStatus === 'notstarted' ? 'Not Started' :
                    gameStatus === 'failed' ? 'Launch Failed' :
                    gameStatus === 'participating' ? 'Minting' :
                    gameStatus === 'waiting' ? 'Waiting' :
                    gameStatus === 'burning' ? 'Burning' :
                    gameStatus === 'claiming' ? 'Claiming' : 'Ended'}
                </StatusBadge>
              </div>
              <StatsGrid>
                <StatCard>
                  <StatValue>{gameData.currentRound} / {gameData.totalRounds}</StatValue>
                  <StatLabel>Round</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{gameData.currentRoundInfo?.totalParticipants}</StatValue>
                  <StatLabel>Total Minter</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{gameData.currentRoundInfo?.totalBurners}</StatValue>
                  <StatLabel>Total Burner</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{gameData.currentRoundInfo?.totalETH ? Number(ethers.formatEther(gameData.currentRoundInfo.totalETH)).toFixed(3) : '0'} {nativeToken}</StatValue>
                  <StatLabel>Minted</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{gameData.currentRoundInfo?.totalBurned ? Number(ethers.formatEther(gameData.currentRoundInfo.totalBurned)).toFixed(3) : '0'} {gameData.tokenSymbol}</StatValue>
                  <StatLabel>Burned</StatLabel>
                </StatCard>

              </StatsGrid>
              <ProgressContainer>
          
                <ProgressHeader>
                  <span>MinCap Progress</span>
                  <span>{progress.toFixed(2)}%</span>
                </ProgressHeader>
                <ProgressBar
                  progress={progress}
                  height="10px"
                  borderRadius="5px"
                  backgroundColor="rgba(255, 255, 255, 0.1)"
                />
              </ProgressContainer>
              {roundProgress.show && (
                <ProgressContainer>
                  <ProgressHeader>
                  <Timer>
                    <span>⏰ Time Remaining:</span>
                    <CountdownValue>{roundProgress.countdownStr}</CountdownValue>
                  </Timer>
                    <span>{roundProgress.progress.toFixed(2)}%</span>
                  </ProgressHeader>
                  <ProgressBar
                    progress={roundProgress.progress}
                    height="10px"
                    borderRadius="5px"
                    backgroundColor="rgba(255, 255, 255, 0.1)"
                  />
                </ProgressContainer>
              )}
              {canRefund && (
                <RefundButton onClick={handleRefund}>
                  Refund {refundAmount} {nativeToken}
                </RefundButton>
              )}
            </RoudInfoStatusCard>
              

            {/* Rewards Distribution Card */}
            <RoudInfoCard>
              <h2 className="text-xl font-bold mb-4">Reward Rankings</h2>
              <RewardsWrapper>
                <RewardsChartContainer>
                  <ChartCenterText>
                    <div className="value">
                    {gameData.currentRoundInfo?.rewardPool && Number(gameData.currentRoundInfo.rewardPool) > 0 
                          ? `${Number(ethers.formatEther(gameData.currentRoundInfo.rewardPool)).toFixed(4)} ${nativeToken}`
                          : `${Number(ethers.formatEther(gameData.totalRewardPool)).toFixed(4)} ${nativeToken}`}</div>
                    <div className="title">Reward Pool</div>
                  </ChartCenterText>
                  <Doughnut 
                    data={rewardsData} 
                    options={chartOptions} 
                  />
                </RewardsChartContainer>
                <RewardsLegend>
                  {rewardsData.labels.map((label, index) => (
                    <LegendItem key={label}>
                      <LegendColor color={rewardsData.datasets[0].backgroundColor[index]} />
                      <span>{label}: {rewardsData.datasets[0].data[index]}%</span>
                    </LegendItem>
                  ))}
                </RewardsLegend>
              </RewardsWrapper>
            </RoudInfoCard>
          </InfoRewardsWrapper>

          {/* Game Operation Section */}
          <GameOperationWrapper hasOperationPanel={gameStatus === 'participating' || gameStatus === 'burning'}>
            {/* Game Status Card */}
            <GameStatusCard>
              <h2 className="text-xl font-bold mb-4">Game Information</h2>
              <GameInfoGrid>
                <InfoItem>
                  <InfoBlock>
                    <InfoLabel>Total Rounds</InfoLabel>
                    <InfoValue>{gameData.totalRounds}</InfoValue>
                  </InfoBlock>
                </InfoItem>
                <InfoItem>
                  <InfoBlock>
                    <InfoLabel>Min Cap</InfoLabel>
                    <InfoValue>{gameData.minCap} {nativeToken}</InfoValue>
                  </InfoBlock>
                </InfoItem>
                <InfoItem>
                  <InfoBlock>
                    <InfoLabel>Minting Time</InfoLabel>
                    <InfoValue>
                      {gameData.roundDuration ? (Number(gameData.roundDuration) / 3600).toFixed(2) : '-'} Hours
                    </InfoValue>
                  </InfoBlock>
                </InfoItem>
                <InfoItem>
                  <InfoBlock>
                    <InfoLabel>Burning Time</InfoLabel>
                    <InfoValue>
                      {gameData.burnDuration ? (Number(gameData.burnDuration) / 3600).toFixed(2) : '-'} Hours
                    </InfoValue>
                  </InfoBlock>
                </InfoItem>
                <InfoItem>
                  <InfoBlock>
                    <InfoLabel>Roll Rate</InfoLabel>
                    <InfoValue>{gameData.rollOverRate}%</InfoValue>
                  </InfoBlock>
                </InfoItem>
              </GameInfoGrid>
            </GameStatusCard>

            {/* Operation Panel */}
            <OperationPanel style={{ display: gameStatus === 'participating' || gameStatus === 'burning' ? 'block' : 'none' }}>
              {gameStatus === 'participating' && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-bold mb-4">Enter Mint Amount</h3>
                    <InputWrapper>
                      <Input
                        type="number"
                        placeholder="0"
                        value={mintAmount}
                        onChange={(e) => setMintAmount(e.target.value)}
                      />

                      <ShareButton onClick={handleMint} >
                        Mint
                      </ShareButton>
                    </InputWrapper>

                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <MintBurnCard>
                      <StatValue>{Number(ethers.formatEther(gameData.totalDeposits)).toFixed(4)} {nativeToken}</StatValue>
                      <StatLabel>Game Total Minted</StatLabel>
                    </MintBurnCard>
                  </div>
                </div>
              )}
              {gameStatus === 'burning' && (
                <div>
                  <div>
                    <h3 className="text-xl font-bold mb-4">Enter Burn Amount</h3>
                    <InputWrapper>
                      <Input
                        type="number"
                        placeholder="0"
                        value={burnAmount}
                        onChange={(e) => setBurnAmount(e.target.value)}
                      />

                      <BurnButton onClick={handleBurn} >
                        Burn
                      </BurnButton>
                    </InputWrapper>

                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <MintBurnCard>
                      <StatValue>{Number(ethers.formatEther(holdingAmount)).toFixed(4)} {gameData.tokenSymbol}</StatValue>
                      <StatLabel>Holding Amount</StatLabel>
                    </MintBurnCard>
                  </div>
                </div>
              )}
            </OperationPanel>
          </GameOperationWrapper>

          {/* Round History */}
          <GameCard>
            <h2 className="text-xl font-bold mb-4">Round History</h2>
            {gameData.rounds.map((round) => (
              <RoundCard key={round.id}>
                <RoundHeader>
                  <RoundTitle>
                    <h3 className="text-lg font-bold">Round {round.id}</h3>
                    <RoundStatus status={round.status as 'active' | 'completed' | 'upcoming'}>
                      {round.status === 'active' ? 'Active' : 
                       round.status === 'completed' ? 'Completed' : 
                       'Upcoming'}
                    </RoundStatus>
                  </RoundTitle>
                  
                  <RoundInfo>
                    <RoundInfoBlock>
                      <RoundInfoLabel>Minter</RoundInfoLabel>
                      <RoundInfoValue>
                        {round.totalParticipants || '0'}
                      </RoundInfoValue>
                    </RoundInfoBlock>
                    <RoundInfoBlock>
                      <RoundInfoLabel>Minted</RoundInfoLabel>
                      <RoundInfoValue>
                        {round.totalDeposit ? `${Number(ethers.formatEther(round.totalDeposit)).toFixed(3)} ${nativeToken}` : `0 ${nativeToken}`}
                      </RoundInfoValue>
                    </RoundInfoBlock>
                    <RoundInfoBlock>
                      <RoundInfoLabel>Burner</RoundInfoLabel>
                      <RoundInfoValue>
                        {round.totalBurners || '0'}
                      </RoundInfoValue>
                    </RoundInfoBlock>
                    <RoundInfoBlock>
                      <RoundInfoLabel>Burned</RoundInfoLabel>
                      <RoundInfoValue>
                        {round.totalBurned ? `${Number(ethers.formatEther(round.totalBurned)).toFixed(3)} ${gameData.tokenSymbol}` : `0 ${gameData.tokenSymbol}`}
                      </RoundInfoValue>
                    </RoundInfoBlock>
                    <RoundInfoBlock>
                      <RoundInfoLabel>Reward Pool</RoundInfoLabel>
                      <RoundInfoValue>
                        {round.rewardPool ? `${Number(ethers.formatEther(round.rewardPool)).toFixed(3)} ${nativeToken}` : `0 ${nativeToken}`}
                      </RoundInfoValue>
                    </RoundInfoBlock> 

                    <ToggleButton 
                      isOpen={openRoundDetails.includes(round.id)}
                      onClick={() => toggleRoundDetail(round.id)}
                    >
                      <span>{openRoundDetails.includes(round.id) ? 'Hide Details' : 'View Details'}</span>
                      <i className="fas fa-chevron-down" />
                    </ToggleButton>
                  </RoundInfo>
                </RoundHeader>

                <RoundDetail isOpen={openRoundDetails.includes(round.id)}>
                  <div>
                    <TableScrollWrapper>
                      <RankTable>
                        <thead>
                          <tr>
                            <th>Rank</th>
                            <th>Address</th>
                            <th>Burned Amount</th>
                            <th>Reward</th>
                          </tr>
                        </thead>
                        <tbody>
                          {winnersData[round.id]?.winners?.length > 0 ? (
                            winnersData[round.id].winners.map((winner, index) => (
                              <tr key={winner}>
                                <td style={{
                                  color: index === 0 ? '#FFD700' : 
                                        index === 1 ? '#4ad0ac' : 
                                        index === 2 ? '#CD7F32' : 'inherit'
                                }}>
                                  {index + 1}
                                </td>
                                <td>{formatAddress(winner)}</td>
                                <td>{Number(winnersData[round.id].burnAmounts[index]).toFixed(4)} {gameData.tokenSymbol}</td>
                                <td>{Number(winnersData[round.id].rewards[index]).toFixed(4)} {nativeToken}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center py-4">
                                {winnersData[round.id] ? (
                                  <p>No winners data available</p>
                                ) : (
                                  <>
                                    <Loading />
                                    <p className="mt-2">Loading winners data...</p>
                                  </>
                                )}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </RankTable>
                    </TableScrollWrapper>

                    <DetailTitle style={{ marginTop: '32px' }}>Your Data</DetailTitle>
                    <UserDataGrid>
                      <DataItem>
                        <div className="label">Minted Amount</div>
                        <div className="value">{userRewardData[round.id]?.roundDeposit || '0'} {nativeToken}</div>
                      </DataItem>
                      <DataItem>
                        <div className="label">Burned Amount</div>
                        <div className="value">{userRewardData[round.id]?.burnAmount || '0'} {gameData.tokenSymbol}</div>
                      </DataItem>
                      <DataItem>
                        <div className="label">Rank</div>
                        <div className="value" style={{ color: userRewardData[round.id]?.isRanked ? '#4ad0ac' : 'inherit' }}>
                          {userRewardData[round.id]?.rank || '-'}
                        </div>
                      </DataItem>
                      <DataItem>
                        <div className="label">Claimable Reward</div>
                        <div className="value">{Number(userRewardData[round.id]?.amount || '0').toFixed(4)} {nativeToken}</div>
                      </DataItem>
                      <DataItem>
                        <div className="label">Claim Status</div>
                        <div className="value">
                          {userRewardData[round.id]?.amount && Number(userRewardData[round.id].amount) > 0 ? (
                            userRewardData[round.id]?.hasClaimedReward ? (
                              <ClaimedButton disabled>Claimed</ClaimedButton>
                            ) : (
                              <ClaimedButton
                                onClick={() => handleClaim(round.id)}
                                disabled={
                                  !(
                                    round.burnEndTime &&
                                    gameData.currentRoundInfo?.currentTime &&
                                    Number(round.burnEndTime) < Number(gameData.currentRoundInfo.currentTime)
                                  )
                                }
                              >
                                Claim
                              </ClaimedButton>
                            )
                          ) : (
                            '-'
                          )}
                        </div>
                      </DataItem>
                    </UserDataGrid>
                  </div>
                </RoundDetail>
              </RoundCard>
            ))}
            {/* {isLoadingMiddleRounds && (
              <div className="text-center py-4">
                <Loading />
                <p className="mt-2">Loading middle rounds...</p>
              </div>
            )} */}
          </GameCard>
        </GameContent>
        {isLoading && <Loading />}
      </GameContainer>
    </>
  );
};

export default GameTest; 